import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export interface SocketUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
}

export interface RealtimeEvent {
  type: 'session_upload' | 'session_update' | 'comment_added' | 'user_joined' | 'user_left' | 'project_updated' | 'collaborator_added';
  data: any;
  userId: string;
  timestamp: Date;
  projectId: string;
}

export interface UserTypingEvent {
  userId: string;
  user: SocketUser;
  type: 'start' | 'stop';
  context: string;
}

export interface CursorUpdateEvent {
  userId: string;
  user: SocketUser;
  position: { x: number; y: number };
  selection?: { start: number; end: number };
  field: string;
}

interface UseSocketOptions {
  projectId?: string;
  autoConnect?: boolean;
}

export const useSocket = (options: UseSocketOptions = {}) => {
  const auth = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState<SocketUser[]>([]);
  const [typingUsers, setTypingUsers] = useState<Map<string, UserTypingEvent>>(new Map());
  const [userCursors, setUserCursors] = useState<Map<string, CursorUpdateEvent>>(new Map());
  const socketRef = useRef<Socket | null>(null);
  const reconnectAttempts = useRef(0);
  const tokenRefreshInProgress = useRef(false);

  // Function to get fresh token
  const getFreshToken = async (): Promise<string | null> => {
    try {
      // First try current token
      let token = localStorage.getItem('accessToken');
      if (token) return token;

      // If no token, try refresh
      if (tokenRefreshInProgress.current) {
        // Wait for ongoing refresh
        return new Promise(resolve => {
          const checkToken = () => {
            if (!tokenRefreshInProgress.current) {
              resolve(localStorage.getItem('accessToken'));
            } else {
              setTimeout(checkToken, 100);
            }
          };
          checkToken();
        });
      }

      tokenRefreshInProgress.current = true;
      await auth?.refreshToken();
      tokenRefreshInProgress.current = false;
      
      return localStorage.getItem('accessToken');
    } catch (error) {
      tokenRefreshInProgress.current = false;
      console.error('Token refresh failed:', error);
      return null;
    }
  };

  // Initialize socket connection
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token || !auth?.user || (options.autoConnect === false)) return;

    // In production, use same-origin (CloudFront) so that /socket.io is proxied to the ALB/backend.
    // In development, use VITE_BACKEND_URL or fallback to localhost.
    const isProd = (import.meta as any).env?.PROD;
    const socketUrl = isProd
      ? (typeof window !== 'undefined' ? window.location.origin : '')
      : ((import.meta as any).env?.VITE_BACKEND_URL || 'http://localhost:3001');

    socketRef.current = io(socketUrl, {
      path: '/socket.io',
      auth: { token },
      transports: ['polling'], // Force polling-only to work around CloudFront WebSocket blocking
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    const socket = socketRef.current;

    // Set up periodic token refresh (every 10 minutes, before 15-minute expiry)
    const tokenRefreshInterval = setInterval(async () => {
      if (socket.connected) {
        console.log('Proactively refreshing Socket.IO token...');
        const freshToken = await getFreshToken();
        if (freshToken) {
          socket.emit('authenticate', freshToken);
        }
      }
    }, 10 * 60 * 1000); // 10 minutes

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      reconnectAttempts.current = 0;

      // Authenticate immediately after connection
      socket.emit('authenticate', token);
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    socket.on('connect_error', async (error: any) => {
      console.error('Socket connection error:', error);
      reconnectAttempts.current++;

      // If this is an authentication error and we haven't exceeded attempts, try token refresh
      if (error?.message?.includes('auth') || error?.type === 'authentication_error') {
        console.log('Authentication error detected, attempting token refresh...');
        const freshToken = await getFreshToken();
        
        if (freshToken && reconnectAttempts.current <= 3) {
          console.log('Retrying connection with fresh token...');
          socket.auth = { token: freshToken };
          socket.connect();
          return;
        }
      }

      if (reconnectAttempts.current > 3) {
        toast.error('Connection lost. Please refresh the page.');
      }
    });

    // Authentication handlers
    socket.on('authenticated', ({ user }: { user: SocketUser }) => {
      console.log('Socket authenticated:', user);

      // Auto-join project if projectId is provided
      if (options.projectId) {
        socket.emit('join_project', options.projectId);
      }
    });

    socket.on('authentication_error', async (error: any) => {
      console.error('Socket authentication error:', error);
      
      // Try to refresh token and re-authenticate
      const freshToken = await getFreshToken();
      if (freshToken) {
        console.log('Retrying authentication with fresh token...');
        socket.emit('authenticate', freshToken);
      } else {
        toast.error('Authentication failed. Please log in again.');
      }
    });

    // Project room handlers
    socket.on('project_users', (users: SocketUser[]) => {
      setActiveUsers(users);
    });

    socket.on('user_joined', ({ user }: { user: SocketUser }) => {
      setActiveUsers(prev => [...prev, user]);
      toast.success(`${user.firstName || user.email} joined the project`);
    });

    socket.on('user_left', ({ user }: { user: SocketUser }) => {
      setActiveUsers(prev => prev.filter(u => u.id !== user.id));
      // Clean up typing and cursor states
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        newMap.delete(user.id);
        return newMap;
      });
      setUserCursors(prev => {
        const newMap = new Map(prev);
        newMap.delete(user.id);
        return newMap;
      });
      toast(`${user.firstName || user.email} left the project`);
    });

    // Typing indicators
    socket.on('user_typing', (event: UserTypingEvent) => {
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        if (event.type === 'start') {
          newMap.set(event.userId, event);
        } else {
          newMap.delete(event.userId);
        }
        return newMap;
      });
    });

    // Cursor updates
    socket.on('cursor_update', (event: CursorUpdateEvent) => {
      setUserCursors(prev => {
        const newMap = new Map(prev);
        newMap.set(event.userId, event);
        return newMap;
      });
    });

    // Real-time events
    socket.on('realtime_event', (event: RealtimeEvent) => {
      // These events should be handled by specific components
      // We'll emit custom events that components can listen to
      window.dispatchEvent(new CustomEvent('realtime-event', { detail: event }));
    });

    // Session upload progress
    socket.on('session_upload_progress', (data: any) => {
      window.dispatchEvent(new CustomEvent('session-upload-progress', { detail: data }));
    });

    // Force disconnect
    socket.on('force_disconnect', ({ projectId, reason }: { projectId: string; reason: string }) => {
      toast.error(`You have been disconnected: ${reason}`);
      socket.emit('leave_project', projectId);
    });

    // Error handling
    socket.on('error', (error: any) => {
      console.error('Socket error:', error);
      toast.error(error);
    });

    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setActiveUsers([]);
      setTypingUsers(new Map());
      setUserCursors(new Map());
    };
  }, [auth?.user, options.projectId, options.autoConnect]);

  // Join project room
  const joinProject = useCallback((projectId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join_project', projectId);
    }
  }, [isConnected]);

  // Leave project room
  const leaveProject = useCallback((projectId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave_project', projectId);
      setActiveUsers([]);
      setTypingUsers(new Map());
      setUserCursors(new Map());
    }
  }, [isConnected]);

  // Send typing indicator
  const sendTyping = useCallback((projectId: string, context: string, isTyping: boolean) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit(isTyping ? 'typing_start' : 'typing_stop', {
        projectId,
        context,
      });
    }
  }, [isConnected]);

  // Send cursor update
  const sendCursorUpdate = useCallback((projectId: string, field: string, position: { x: number; y: number }, selection?: { start: number; end: number }) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('cursor_update', {
        projectId,
        field,
        position,
        selection,
      });
    }
  }, [isConnected]);

  // Send session update
  const sendSessionUpdate = useCallback((projectId: string, data: any) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('session_update', {
        projectId,
        ...data,
      });
    }
  }, [isConnected]);

  // Send comment
  const sendComment = useCallback((projectId: string, data: any) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('comment_added', {
        projectId,
        ...data,
      });
    }
  }, [isConnected]);

  // Send upload progress
  const sendUploadProgress = useCallback((projectId: string, sessionId: string, progress: number) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('session_upload_progress', {
        projectId,
        sessionId,
        progress,
      });
    }
  }, [isConnected]);

  return {
    socket: socketRef.current,
    isConnected,
    activeUsers,
    typingUsers: Array.from(typingUsers.values()),
    userCursors: Array.from(userCursors.values()),
    joinProject,
    leaveProject,
    sendTyping,
    sendCursorUpdate,
    sendSessionUpdate,
    sendComment,
    sendUploadProgress,
  };
};